
import { defineMessages } from 'react-intl';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import shuffle from 'lodash/shuffle';
import round from 'lodash/round';

import store from 'Redux/store';
import { currencyCode } from 'Helpers/Geo/data';
import env from 'Env';

const messages = defineMessages({
  inLibrary: {
    id: 'pickandmixhelper.inLibrary',
    defaultMessage: 'In Library',
  },
  inSteamLibrary: {
    id: 'pickandmixhelper.inSteamLibrary',
    defaultMessage: 'In Steam Library',
  },
  lowStock: {
    id: 'pickandmixhelper.lowStock',
    defaultMessage: 'Low Stock',
  },
  soldOut: {
    id: 'pickandmixhelper.soldOut',
    defaultMessage: 'Sold Out',
  },
  all: {
    id: 'pickandmixhelper.all',
    defaultMessage: 'All',
  },
  recommended: {
    id: 'pickandmixhelper.recommended',
    defaultMessage: 'Recommended',
  },
  recommendedForYou: {
    id: 'pickandmixhelper.recommendedForYou',
    defaultMessage: 'Recommended For You',
  },
  recommendedForYouDesc: {
    id: 'pickandmixhelper.recommendedForYouDesc',
    defaultMessage: 'Explore a curated selection recommended for you',
  },
  steamdeckverified: {
    id: 'pickandmixhelper.steamdeckverified',
    defaultMessage: 'Steam Deck Verified',
  },
  steamdeckverifiedDesc: {
    id: 'pickandmixhelper.steamdeckverifiedDesc',
    defaultMessage: 'Optimised for your Steam Deck! Enjoy a selection of titles fully verified to play on the go.',
  },
  bestSaving: {
    id: 'pickandmixhelper.bestSaving',
    defaultMessage: 'Best Saving',
  },
  bestSavingDesc: {
    id: 'pickandmixhelper.bestSavingDesc',
    defaultMessage: 'Enjoy unbeatable savings with this collection of top picks at a fantastic price',
  },
  remove: {
    id: 'pickandmixhelper.remove',
    defaultMessage: 'Remove from cart',
  },
  viewBundle: {
    id: 'pickandmixhelper.viewBundle',
    defaultMessage: 'View Bundle',
  },
  fanFavourites: {
    id: 'pickandmixhelper.fanFavourites',
    defaultMessage: 'Fan Favourites',
  },
  fanFavouritesDesc: {
    id: 'pickandmixhelper.fanFavouritesDesc',
    defaultMessage: 'Discover the most popular picks from the community',
  },
  criticallyAcclaimed: {
    id: 'pickandmixhelper.criticallyAcclaimed',
    defaultMessage: 'Critically Acclaimed',
  },
  criticallyAcclaimedDesc: {
    id: 'pickandmixhelper.criticallyAcclaimedDesc',
    defaultMessage: 'Explore the best of the best, as rated by critics',
  },
  categoriesBundleTitle: {
    id: 'pickandmixhelper.bundletitle',
    defaultMessage: '{categoryName} Bundle',
  },
  categoriesDesc: {
    id: 'pickandmixhelper.gamesDesc',
    defaultMessage: 'Explore a top selection of {categoryName} titles, perfect for fans of the genre',
  },
  publishersDesc: {
    id: 'pickandmixhelper.publishersDesc',
    defaultMessage: 'Explore a diverse lineup from {categoryName}, featuring a range of popular and acclaimed titles',
  },

  franchisesDesc: {
    id: 'pickandmixhelper.franchisesDesc',
    defaultMessage: 'Immerse yourself in the world of {categoryName} with this curated collection of fan-favorite titles.',
  },
  games: {
    id: 'pickandmixhelper.games',
    defaultMessage: 'Games',
  },
  products: {
    id: 'pickandmixhelper.products',
    defaultMessage: 'Products',
  },
  dlcs: {
    id: 'pickandmixhelper.dlcs',
    defaultMessage: 'DLCs',
  },
  pickForMeDesc: {
    id: 'pickandmixhelper.pickForMeRecommendationMessage',
    defaultMessage: 'Here are our recommendations to you. Add them to your cart or pick again to see something new.',
  },
  pickForMeDescBased: {
    id: 'pickandmixhelper.pickForMeDescBased',
    defaultMessage: 'Here are our recommendations to you based on {categoryName}. Add them to your cart or pick again to see something new.',
  },
  pickForMeDescBasedBundleType: {
    id: 'pickandmixhelper.pickForMeDescBasedBundleType',
    defaultMessage: 'Here are our recommendations to you based on {categoryName} {bundleType}. Add them to your cart or pick again to see something new.',
  },
  pickForMeDescFrom: {
    id: 'pickandmixhelper.pickForMeDescFrom',
    defaultMessage: 'Here are our recommendations to you from {categoryName}. Add them to your cart or pick again to see something new.',
  },
});

/**
 * Retrieves file data from the provided downloads array.
 *
 * @param {Array} downloads - Array of downloads
 * @returns {Object} - Object containing the file count and download types
 */
const getFileData = (downloads) => {
  let productDownloads = [];
  let fileCount = 0;
  if (downloads) {
    productDownloads = downloads.filter(download => !download.is_sample && !download.is_group);
  }

  const downloadTypes = [];
  productDownloads.forEach((download) => {
    download.files.forEach((file) => {
      fileCount += 1;
      if (downloadTypes.includes(downloadTypes)) return;
      if (file.format === 'zip') return;
      downloadTypes.push(file.format);
    });
  });

  return { fileCount, downloadTypes };
};


/**
 * checks what type of bundle the Pick and Mix is
 * @param {string} type
 * @param {Array.Object} products
 * @returns {string} - The type of bundle
 */
const checkBundleType = (type, products) => {
  if (Array.isArray(products) && products.length > 0) {
    const isDlcBundle = products.every(product => product.type === 'dlc' || product.display_type === 'dlc');
    if (isDlcBundle) return 'dlc-bundle';
  }

  if (type === 'bundle') {
    return 'game-bundle';
  }

  return 'product-bundle';
};

/**
 * Retrieves the message for the bundle type
 * @param {string} type - The type of bundle
 * @param {Array.Object} products - The products in the bundle
 * @returns {string} - The message for the bundle type
 * */
const getBundleTypeMessage = (type, products) => {
  const bundleType = checkBundleType(type, products);

  switch (bundleType) {
    case 'game-bundle':
      return messages.games;
    case 'dlc-bundle':
      return messages.dlcs;
    default:
      return messages.products;
  }
};

/**
 * Retrieves the label data for a hit card
 *
 * @param {boolean} lowStock - Indicates if the card has low stock.
 * @param {boolean} soldOut - Indicates if the card is sold out.
 * @param {boolean} inLibrary - Indicates if the card is in the user's library.
 * @param {boolean} inSteamLibrary - Indicates if the card is in the user's Steam library.
 * @returns {Object} - Object containing the message, label, labelClass, and shouldShowLabel properties.
 */
const getCardLabelData = (lowStock, soldOut, inLibrary, inSteamLibrary) => {
  let message = null;
  let label = null;
  let labelClass = 'low-stock-label';

  if (lowStock) {
    label = 'lowStock';
    message = messages.lowStock;
    labelClass = 'low-stock-label';
  }

  if (inLibrary) {
    label = 'inLibrary';
    message = messages.inLibrary;
    labelClass = 'inLibrary-label';
  } else if (inSteamLibrary) {
    label = 'inSteamLibrary';
    message = messages.inSteamLibrary;
    labelClass = 'inSteamLibrary-label';
  }

  if (soldOut) {
    label = 'soldOut';
    message = messages.soldOut;
    labelClass = 'low-stock-label';
  }

  return { message, label, labelClass, shouldShowLabel: !!label };
};


/**
 * searches through all Pick and Mix objects stored in the Redux state
 * and returns the first Pick and Mix object that contains a product with the specified Pick and Mix ID.
 *
 * @param {string} productId - The ID of Pick and mix to search.
 * @returns {Object|undefined} - The Pick and Mix object containing the product with the specified ID or undefined if no match is found.
 */
const findPickAndMixByProductId = (productId) => {
  const reduxStore = store.getState();
  const allPickAndMixes = _get(reduxStore, 'pickAndMix.all', []) || [];
  let matchingPickAndMix;

  allPickAndMixes.forEach((pnm) => {
    const pickAndMixProducts = pnm.products;
    const hasMatch = pickAndMixProducts.find(product => product._id === productId);

    if (hasMatch) matchingPickAndMix = pnm;
  });

  return matchingPickAndMix;
};

/**
 * Sorts the pick and mix products based on user library and sold out status
 * @param {Array.object} products - Array of products to sort
 * @param {Array.string} userLibrary - Array of user library product names
 * @param {Array.string} steamLibrary - Array of steam library product names
 * @param {boolean} wishlistSlugs - Array of slugs in the user's wishlist
 * @returns {Array.object} - Sorted array of products
 */
const sortPickAndMixProducts = (products, userLibrary, steamLibrary, wishlistSlugs) => {
  const productsToSort = [...products];
  let purchaseHistoryNames = userLibrary || [];

  if (!isEmpty(steamLibrary)) {
    purchaseHistoryNames = purchaseHistoryNames.concat(steamLibrary);
  }


  const hasPurchased = !!productsToSort.find(hit => purchaseHistoryNames.includes(hit.name));
  const inStockHits = productsToSort.filter(product => !product.soldOut);
  const unPurchasedProducts = inStockHits.filter(hit => !purchaseHistoryNames.includes(hit.name));
  const purchasedProducts = inStockHits.filter(hit => purchaseHistoryNames.includes(hit.name));
  const soldOutProducts = productsToSort.filter(hit => hit.soldOut);

  let productsToDisplay = unPurchasedProducts;

  const wishlistProducts = productsToDisplay.filter(p => wishlistSlugs.includes(p.slug));
  const nonWishlistProducts = productsToDisplay.filter(p => !wishlistSlugs.includes(p.slug));

  if (!isEmpty(wishlistSlugs)) {
    productsToDisplay = wishlistProducts.concat(nonWishlistProducts);
  }

  if (hasPurchased) productsToDisplay = productsToDisplay.concat(purchasedProducts);

  productsToDisplay = productsToDisplay.concat(soldOutProducts);
  return productsToDisplay;
};

/**
 * Combines the regions_included and regions_excluded from an array of products
 * @param {Array.object} products - Array of products to combine
 * @returns {Object} - Object containing the combined regionsIncluded and regionsExcluded
 */
const combineProductRegions = (products) => {
  const allRegionsIncluded = products.flatMap(product => product.regions_included || []);
  const allRegionsExcluded = products.flatMap(product => product.regions_excluded || []);

  const getUniqRegions = regions => regions.filter((region, index, self) =>
    index === self.findIndex(selfRegion => (selfRegion.code === region.code)),
  );

  const regionsIncluded = getUniqRegions(allRegionsIncluded) || [];
  const regionsExcluded = getUniqRegions(allRegionsExcluded) || [];

  return { regionsIncluded, regionsExcluded };
};

/**
  * Returns the title and description for the categories bundle
  * @param {string} type - The type of category
  * @returns {Object} - Object containing the title and description
  */
const getCategoriesTitleAndDesc = (type) => {
  const base = {
    title: messages.categoriesBundleTitle,
    desc: messages.categoriesDesc,
    pickForMeDesc: messages.pickForMeDesc,
  };

  switch (type) {
    case 'publishers':
      base.desc = messages.publishersDesc;
      base.pickForMeDesc = messages.pickForMeDescFrom;
      break;
    case 'franchises':
      base.desc = messages.franchisesDesc;
      base.pickForMeDesc = messages.pickForMeDescFrom;
      break;
    case 'categories':
      base.pickForMeDesc = messages.pickForMeDescBasedBundleType;
      break;
    default:
      break;
  }

  return base;
};

/**
 * Generates a preselected bundle link based on the provided slugs
 * @param {Array.string} slugs - Array of slugs
 * @returns {string|boolean} - The generated preselected bundle link or false if no slugs are provided
 */
const generatedPreselectedBundleLink = (slugs) => {
  if (!Array.isArray(slugs) || slugs?.length === 0) return false;
  let slugStrings = '';
  slugStrings = slugs.join(',');

  return `?preselected=${slugStrings}`;
};

/**
 * Calculates pricing and relevant data for a preselected bundle
 *  @param {Array.object} sortedHits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const calculatePreselectedBundle = (sortedHits, tierQuantity, tierPrice, slug) => {
  const selectedHits = sortedHits.slice(0, tierQuantity);
  const fullPrice = selectedHits.reduce((total, p) => total + p.fullPrice[currencyCode], 0);
  const saving = fullPrice - tierPrice;
  const percentSaved = saving / fullPrice;

  const generatedLink = generatedPreselectedBundleLink(selectedHits.map(hit => hit.slug));
  const preselectedLink = `${slug}${generatedLink}`;

  return { selectedHits, fullPrice, percentSaved, preselectedLink };
};

/**
 * Returns a preselected bundle based on the recommended for you criteria
 *  @param {Array.object} hits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param { boolean } shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getRecommendedForYou = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0) return null;

  const shuffledHits = shuffleAll ? shuffle(hits).slice(0, 6) : shuffle(hits.slice(0, 6));

  if (shuffledHits.length < tierQuantity) return null;

  const {
    selectedHits,
    fullPrice,
    percentSaved,
    preselectedLink,
  } = calculatePreselectedBundle(shuffledHits, tierQuantity, tierPrice, slug);

  if (selectedHits.length < tierQuantity) return null;

  return {
    hits: selectedHits,
    title: messages.recommendedForYou,
    desc: messages.recommendedForYouDesc,
    fullPrice,
    percentSaved,
    price: tierPrice,
    type,
    preselectedLink,
    pickForMeDesc: messages.pickForMeDesc,
  };
};

/**
 * Returns a preselected bundle based on the fan favourites criteria
 * @param {Array.object} hits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param {boolean} shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getFanFavourites = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0) return null;

  const fanFavouritesHits = hits.filter(hit => hit.rating_score >= 4);

  if (fanFavouritesHits.length < tierQuantity) return null;

  const sortedHits = [
    ...fanFavouritesHits.sort((a, b) => (b.rating_score || 0) - (a.rating_score || 0)),
  ];

  const hitsToUse = shuffleAll ? fanFavouritesHits : sortedHits;

  const {
    selectedHits,
    fullPrice,
    percentSaved,
    preselectedLink,
  } = calculatePreselectedBundle(hitsToUse, tierQuantity, tierPrice, slug);

  if (selectedHits.length < tierQuantity) return null;

  return {
    hits: selectedHits,
    title: messages.fanFavourites,
    desc: messages.fanFavouritesDesc,
    fullPrice,
    percentSaved,
    price: tierPrice,
    type,
    preselectedLink,
    pickForMeDesc: messages.pickForMeDesc,
  };
};

/**
 * Returns a preselected bundle based on the critically acclaimed criteria
 * @param {Array.object} hits - Array of hits to calculate the bundle from
 *  @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param {boolean} shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getCriticallyAcclaimed = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0) return null;

  const recommendedThreshold = env.percentRecommendedThreshold;

  const accalimedHits = hits.filter(hit => hit.metacritic_score >= recommendedThreshold);

  if (accalimedHits.length < tierQuantity) return null;

  const sortedHits = [
    ...accalimedHits.sort((a, b) => (b.metacritic_score || 0) - (a.metacritic_score || 0)),
  ];

  const hitsToUse = shuffleAll ? shuffle(accalimedHits) : sortedHits;

  const {
    selectedHits,
    fullPrice,
    percentSaved,
    preselectedLink,
  } = calculatePreselectedBundle(hitsToUse, tierQuantity, tierPrice, slug);

  if (selectedHits.length < tierQuantity) return null;

  return {
    hits: selectedHits,
    title: messages.criticallyAcclaimed,
    desc: messages.criticallyAcclaimedDesc,
    price: tierPrice,
    percentSaved,
    fullPrice,
    type,
    preselectedLink,
    pickForMeDesc: messages.pickForMeDesc,
  };
};

/**
 * Returns a preselected bundle based on the steam deck verified criteria
 * @param {Array.object} hits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param {boolean} shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getSteamDeckVerified = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0) return null;

  const hitsToUse = shuffleAll ? shuffle(hits) : hits;
  const steamDeckHits = hitsToUse.filter(hit => hit.steam_deck_support === 'verified');

  if (steamDeckHits.length < tierQuantity) return null;


  const {
    selectedHits,
    fullPrice,
    percentSaved,
    preselectedLink,
  } = calculatePreselectedBundle(steamDeckHits, tierQuantity, tierPrice, slug);

  if (selectedHits.length < tierQuantity) return null;

  return {
    hits: selectedHits,
    title: messages.steamdeckverified,
    desc: messages.steamdeckverifiedDesc,
    price: tierPrice,
    percentSaved,
    fullPrice,
    type,
    preselectedLink,
    pickForMeDesc: messages.pickForMeDesc,
  };
};

/**
 * Returns a preselected bundle based on the categories including (categories|franchises|publishers) criteria
 * @param {Array.object} hits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param {string} type - The type of category
 * @param {boolean} shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getCategories = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0 || !type) return null;

  const allCategories = hits.flatMap(hit => hit[type]).filter(Boolean);

  if (!Array.isArray(allCategories) || allCategories.length === 0) return null;

  const categoryCountObj = allCategories.reduce((count, category) => {
    count[category] = (count[category] || 0) + 1;
    return count;
  }, {});

  const categoryKeys = Object.keys(categoryCountObj);

  const categoryCountArr = categoryKeys
    .map(c => ({
      name: c, count: categoryCountObj[c], hits: hits.filter(h => h[type].includes(c)),
    }))
    .filter(c => c.count >= tierQuantity);

  const topCategories = [
    ...categoryCountArr].sort((a, b) => b.count - a.count);

  const categoriesToUse = shuffleAll ? shuffle(categoryCountArr) : topCategories;

  if (!Array.isArray(categoriesToUse) || categoriesToUse.length === 0) return null;

  const preselectedCategories = categoriesToUse.slice(0, 3).map((category) => {
    const {
      selectedHits,
      fullPrice,
      percentSaved,
      preselectedLink,
    } = calculatePreselectedBundle(category.hits, tierQuantity, tierPrice, slug);

    const { title, desc } = getCategoriesTitleAndDesc(type);

    if (selectedHits.length < tierQuantity) return null;

    return {
      hits: selectedHits,
      title,
      desc,
      categoryName: category.name,
      percentSaved,
      price: tierPrice,
      fullPrice,
      type,
      preselectedLink,
      pickForMeDesc: messages.pickForMeDesc,
    };
  });

  return preselectedCategories;
};

/**
 * Returns a preselected bundle based on the best saving criteria
 * @param {Array.object} hits - Array of hits to calculate the bundle from
 * @param {number} tierQuantity - The quantity of hits to include in the bundle
 * @param {number} tierPrice - The price of the tier
 * @param {string} slug - The slug of the Pick and Mix
 * @param {string} type - The type of preselection
 * @param {boolean} shuffleAll - Shuffle all hits
 * @returns {Object} - Object containing the selected hits, full price, percent saved, and preselected link
 */
const getBestSaving = (hits, tierQuantity, tierPrice, slug, type, shuffleAll) => {
  if (!Array.isArray(hits) || hits?.length === 0) return null;

  const hitsToUse = shuffleAll ? shuffle(hits) : hits;

  const sortedHits = [
    ...hitsToUse
      .sort((a, b) => (b.fullPrice?.[currencyCode] || 0) - (a.fullPrice?.[currencyCode] || 0)),
  ];

  const {
    selectedHits,
    fullPrice,
    percentSaved,
    preselectedLink,
  } = calculatePreselectedBundle(sortedHits, tierQuantity, tierPrice, slug);

  if (selectedHits.length < tierQuantity) return null;

  return {
    hits: selectedHits,
    title: messages.bestSaving,
    desc: messages.bestSavingDesc,
    percentSaved,
    price: tierPrice,
    fullPrice,
    type,
    preselectedLink,
    pickForMeDesc: messages.pickForMeDesc,
  };
};

/**
 * Calculates upsell details required to create a preselected bundle
 * This function determines which products should be preselected for a Pick and Mix offer
 * based on the user's purchase history, available stock, and the tiers of the offer.
 * It filters out products that are already selected, sold out, or previously purchased by the user.
 *
 * @param {Array} hits - The list of available products for the Pick and Mix offer.
 * @param {Array} tiers - The tiers of the Pick and Mix offer, each containing a quantity of products.
 * @param {Array} selectedProducts - The list of products that have already been selected by the user.
 * @param {Array} userLibrary - The list of products in the user's library.
 * @param {Array} steamLibrary - The list of products in the user's Steam library.
 * @param {string} slug - The slug identifier for the Pick and Mix offer.
 * @returns {Object} - An object containing the preselected products and related information, or null if no valid preselection can be made.
 */
const calculateUpsellDetails = (
  userLibrary, steamLibrary, hits, tiers, selectedProducts,
) => {
  const purchaseHistoryNames = [
    ...(Array.isArray(userLibrary) ? userLibrary : []),
    ...(Array.isArray(steamLibrary) ? steamLibrary : []),
  ];

  const intialState = { unPurchasedHits: [], upsellPrice: 0, quantity: 0 };

  if (!Array.isArray(hits) || hits?.length === 0) return intialState;
  if (!Array.isArray(tiers) || tiers?.length === 0) return intialState;

  const selectedProductSlugs = selectedProducts.map(p => p.slug);

  const unSelectedHits = hits.filter(hit => !selectedProductSlugs.includes(hit.slug));
  const inStockHits = unSelectedHits.filter(product => !product.soldOut);
  const unPurchasedHits = inStockHits.filter(hit => !purchaseHistoryNames.includes(hit.name));
  const firstTier = tiers?.[0];
  const tierQuantity = firstTier?.quantity;
  const selectedProductsCount = Array.isArray(selectedProducts) ? selectedProducts.length : 0;
  const potentialTier = [...tiers]
    .sort((a, b) => b.quantity - a.quantity)
    .find(t => (selectedProductsCount + tierQuantity) >= t.quantity);

  const startingTier = [...tiers]
    .sort((a, b) => b.quantity - a.quantity)
    .find(t => (selectedProductsCount) >= t.quantity) || firstTier;

  const startingItemPrice = round((startingTier.price[currencyCode] / startingTier.quantity), 2);
  const potentialItemPrice = round((potentialTier.price[currencyCode] / potentialTier.quantity), 2);

  const potentialTierPrice =
    round((potentialItemPrice * (selectedProductsCount + tierQuantity)) / 100, 2);
  const startingTierPrice =
    round((startingItemPrice * (selectedProductsCount)) / 100, 2);

  const upsellPrice = potentialTierPrice - startingTierPrice;

  return { unPurchasedHits, upsellPrice, tierQuantity };
};

/**
 * Generates preselected products for a Pick and Mix.
 * @param {Array} hits - The list of available products for the Pick and Mix offer.
 * @param {Array} tiers - The tiers of the Pick and Mix offer, each containing a quantity of products.
 * @param {Array} selectedProducts - The list of products that have already been selected by the user.
 * @param {Array} userLibrary - The list of products in the user's library.
 * @param {Array} steamLibrary - The list of products in the user's Steam library.
 * @param {string} slug - The slug identifier for the Pick and Mix offer.
 * @returns {Object|null} - An object containing the preselected products and related information, or null if no valid preselection can be made.
 */
const getPreselections = (hits, tiers, selectedProducts, userLibrary, steamLibrary, slug) => {
  const { upsellPrice, unPurchasedHits, tierQuantity } =
    calculateUpsellDetails(userLibrary, steamLibrary, hits, tiers, selectedProducts);

  if (!Array.isArray(unPurchasedHits) || unPurchasedHits?.length === 0) return null;

  const preselectionsFuncs = [
    { func: getRecommendedForYou, type: 'recommendedForYou' },
    { func: getSteamDeckVerified, type: 'steamDeckVerified' },
    { func: getFanFavourites, type: 'fanFavourites' },
    { func: getCriticallyAcclaimed, type: 'criticallyAcclaimed' },
    { func: getBestSaving, type: 'bestSaving' },
    { func: getCategories, type: 'categories' },
    { func: getCategories, type: 'publishers' },
    { func: getCategories, type: 'franchises' },
  ];

  const preselections = [];

  preselectionsFuncs.forEach((pFunc) => {
    const preselection = pFunc.func(unPurchasedHits, tierQuantity, upsellPrice, slug, pFunc.type);
    const isCategoryGroup = Array.isArray(preselection);

    if (!preselection) return;

    if (isCategoryGroup) {
      preselection.forEach((group) => {
        if (group) preselections.push(group);
      });
    } else {
      preselections.push(preselection);
    }
  });

  const cleanPreselections = preselections.filter(Boolean);
  return cleanPreselections;
};


export {
  getFileData,
  getCardLabelData,
  findPickAndMixByProductId,
  sortPickAndMixProducts,
  messages,
  getPreselections,
  generatedPreselectedBundleLink,
  combineProductRegions,
  checkBundleType,
  getBundleTypeMessage,
};
